import { useAuth } from 'react-oidc-context';
import { useMemo, useEffect, useState } from 'react';
import { fetchUserAttributesFromCognito } from '../services/cognitoService';

/**
 * Hook para acessar dados do usuário autenticado do Cognito
 * Extrai dados do profile, do id_token decodificado e busca atributos adicionais via API
 */
export const useUserData = () => {
  const auth = useAuth();
  const [additionalAttributes, setAdditionalAttributes] = useState<any>(null);

  // Função para decodificar JWT
  const decodeJWT = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Erro ao decodificar JWT:', error);
      return null;
    }
  };

  // Decodifica o id_token se disponível
  const idTokenClaims = useMemo(() => {
    if (auth.user?.id_token) {
      return decodeJWT(auth.user.id_token);
    }
    return null;
  }, [auth.user?.id_token]);

  // Busca atributos adicionais do Cognito quando o usuário estiver autenticado
  useEffect(() => {
    const loadAdditionalAttributes = async () => {
      if (auth.isAuthenticated && auth.user?.access_token && !additionalAttributes) {
        try {
          const attrs = await fetchUserAttributesFromCognito(auth.user.access_token);
          if (attrs) {
            setAdditionalAttributes(attrs);
            console.log('✅ Atributos adicionais carregados:', attrs);
          }
        } catch (error) {
          console.error('Erro ao carregar atributos adicionais:', error);
        }
      }
    };

    loadAdditionalAttributes();
  }, [auth.isAuthenticated, auth.user?.access_token, additionalAttributes]);

  // Extrai dados do usuário de múltiplas fontes
  const userData = useMemo(() => {
    if (!auth.isAuthenticated || !auth.user) {
      return null;
    }

    const profile = auth.user.profile || {};
    const claims = idTokenClaims || {};
    const attrs = additionalAttributes || {};

    return {
      // ID do usuário
      sub: profile.sub || claims.sub || attrs.sub,
      
      // Email
      email: profile.email || claims.email || attrs.email || profile.preferred_username || claims.preferred_username || attrs.preferred_username,
      
      // Nome completo - tenta múltiplas fontes
      name: attrs.name || 
            profile.name || 
            claims.name || 
            attrs['custom:name'] ||
            (attrs.given_name && attrs.family_name ? `${attrs.given_name} ${attrs.family_name}` : null) ||
            (profile.given_name && profile.family_name ? `${profile.given_name} ${profile.family_name}` : null) ||
            profile.given_name || 
            claims.given_name || 
            attrs.given_name ||
            profile.family_name || 
            claims.family_name || 
            attrs.family_name,
      givenName: attrs.given_name || profile.given_name || claims.given_name,
      familyName: attrs.family_name || profile.family_name || claims.family_name,
      
      // Telefone - tenta múltiplas fontes
      phoneNumber: attrs.phone_number || 
                   attrs.phone || 
                   attrs['custom:phone_number'] ||
                   profile.phone_number || 
                   claims.phone_number || 
                   profile.phone || 
                   claims.phone,
      
      // Outros dados
      username: attrs.preferred_username || profile.preferred_username || claims.preferred_username || attrs.username || profile.username || claims.username,
      
      // Tokens
      accessToken: auth.user.access_token,
      idToken: auth.user.id_token,
      expiresAt: auth.user.expires_at,
      
      // Dados completos para debug
      fullProfile: profile,
      fullIdTokenClaims: claims,
      additionalAttributes: attrs,
    };
  }, [auth.isAuthenticated, auth.user, idTokenClaims, additionalAttributes]);

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: userData,
    auth, // Acesso completo ao objeto auth se necessário
  };
};

